import { AuthDialog } from "@/components/AuthDialog";
import { ReportButton } from "@/components/ReportButton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { normalizeImageUrl } from "@/lib/image-url";
import { queryClient } from "@/lib/queryClient";
import type { Comment } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  ArrowUpDown,
  Lock,
  LogIn,
  MessageSquare,
  Send,
  ThumbsUp,
} from "lucide-react";
import { useState } from "react";

interface CommentsProps {
  articleId: string;
  targetType?:
    | "article"
    | "market_listing"
    | "community_talk"
    | "city_classified"
    | "community_post"
    | "community_event";
  postAuthorId?: string; // For private comments (only visible to post author)
  isPostAuthor?: boolean; // Whether current user is the post author
  responseMode?: "comment" | "answer" | "followup";
}

export function Comments({
  articleId,
  targetType = "article",
  postAuthorId,
  isPostAuthor = false,
  responseMode = "comment",
}: CommentsProps) {
  const [content, setContent] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | number | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [sortOrder, setSortOrder] = useState<"latest" | "oldest">("latest");
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const responseCopy =
    responseMode === "answer"
      ? {
          noun: "답변",
          header: "답변",
          login: "답변을 작성하려면 로그인이 필요합니다.",
          placeholder: "상대방이 확인할 수 있는 근거와 실제 경험을 포함해 답변해주세요.",
          emptyTitle: "아직 답변이 없어요",
          emptyDescription: "알고 있는 절차나 경험이 있다면 첫 답변을 남겨보세요.",
          submit: "답변 등록",
        }
      : responseMode === "followup"
        ? {
            noun: "추가 질문·의견",
            header: "추가 질문·의견",
            login: "추가 질문이나 의견을 남기려면 로그인이 필요합니다.",
            placeholder: "해결 과정에서 더 궁금한 점이나 도움이 된 부분을 남겨주세요.",
            emptyTitle: "아직 추가 질문이나 의견이 없어요",
            emptyDescription: "궁금한 점이나 비슷한 경험을 첫 번째로 나눠보세요.",
            submit: "질문·의견 등록",
          }
        : {
            noun: "댓글",
            header: "댓글",
            login: "댓글을 작성하려면 로그인이 필요합니다.",
            placeholder: "댓글을 입력하세요. 욕설·비방·허위 정보는 금지됩니다.",
            emptyTitle: "아직 댓글이 없어요",
            emptyDescription: "가장 먼저 의견을 남겨보세요.",
            submit: "댓글 등록",
          };

  // Check if private comments are allowed (only for community_talk, market_listing, and community_post)
  const canUsePrivateComment =
    (targetType === "community_talk" ||
      targetType === "market_listing" ||
      targetType === "community_post") &&
    !isPostAuthor;

  const { data: comments = [], isLoading } = useQuery<Comment[]>({
    queryKey: ["/api/comments", targetType, articleId, user?.uid, sortOrder],
    queryFn: async () => {
      // Add userId to query params for private comment visibility
      const params = new URLSearchParams({
        targetType: targetType,
        targetId: articleId,
      });

      // Add userEmail if available (for private comment visibility)
      if (user?.email) {
        params.append("userEmail", user.email);
      }

      const response = await fetch(`/api/comments?${params.toString()}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch comments");
      }
      const data = await response.json();
      const comments = data.comments || [];
      // Sort comments based on sortOrder
      if (sortOrder === "oldest") {
        return [...comments].sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      }
      return comments; // latest (default, already sorted by API)
    },
    staleTime: 30000, // 30초간 fresh로 간주
    gcTime: 5 * 60 * 1000, // 5분간 캐시 유지
    refetchOnWindowFocus: false, // 윈도우 포커스 시 자동 refetch 방지
    refetchOnMount: true, // 마운트 시에만 refetch
    enabled: true,
  });

  const createMutation = useMutation({
    mutationFn: async (data: {
      userId: string;
      userEmail: string;
      userDisplayName: string;
      userPhotoUrl: string | null;
      content: string;
      isPrivate?: boolean;
    }) => {
      // Validate required fields
      if (!data.userId) {
        console.error("[Comments] Missing userId:", data);
        throw new Error("사용자 ID가 없습니다. 로그인 상태를 확인해주세요.");
      }

      const requestBody: any = {
        targetType: targetType,
        targetId:
          typeof articleId === "string"
            ? parseInt(articleId) || articleId
            : articleId,
        firebaseUid: data.userId, // Firebase UID for user lookup (required)
        content: data.content,
        isPrivate: isPrivate ? 1 : 0, // Include isPrivate flag
      };

      // Only include email if it has a non-empty value
      if (data.userEmail && data.userEmail.trim()) {
        requestBody.email = data.userEmail.trim();
      }

      // Include display name if available (for user creation)
      if (data.userDisplayName && data.userDisplayName.trim()) {
        requestBody.userDisplayName = data.userDisplayName.trim();
      }

      // Debug logging
      console.log("[Comments] Sending request:", {
        hasFirebaseUid: !!requestBody.firebaseUid,
        firebaseUid: requestBody.firebaseUid
          ? `${requestBody.firebaseUid.substring(0, 8)}...`
          : undefined,
        hasEmail: !!requestBody.email,
        email: requestBody.email
          ? `${requestBody.email.substring(0, 10)}...`
          : undefined,
        targetType: requestBody.targetType,
        targetId: requestBody.targetId,
      });

      const response = await fetch(`/api/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("[Comments] API Error Response:", {
          status: response.status,
          statusText: response.statusText,
          errorData,
          requestBody: {
            hasFirebaseUid: !!requestBody.firebaseUid,
            firebaseUid: requestBody.firebaseUid
              ? `${requestBody.firebaseUid.substring(0, 8)}...`
              : undefined,
            hasEmail: !!requestBody.email,
            targetType: requestBody.targetType,
            targetId: requestBody.targetId,
          },
        });
        const error = new Error(
          errorData.message || errorData.error || "댓글 작성에 실패했습니다."
        );
        (error as any).status = response.status;
        (error as any).response = response;
        (error as any).errorData = errorData;
        throw error;
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/comments", targetType, articleId, user?.uid],
      });
      setContent("");
      setIsPrivate(false);
      toast({
        title: `${responseCopy.noun} 작성 완료`,
        description: `${responseCopy.noun}이 등록되었습니다.`,
      });
    },
    onError: (error: any) => {
      let errorMessage = error?.message || "댓글 작성에 실패했습니다.";

      // Handle specific error cases
      if (error?.status === 401) {
        errorMessage = "로그인이 필요합니다. 로그인 후 다시 시도해주세요.";
      } else if (error?.status === 400) {
        errorMessage = error?.message || "입력 내용을 확인해주세요.";
      } else if (error?.status === 409) {
        errorMessage = "동일한 댓글을 5분 이내에 작성할 수 없습니다.";
      }

      toast({
        title: "오류",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const replyMutation = useMutation({
    mutationFn: async (data: { parent_id: number | string; body: string }) => {
      if (!user) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch("/api/comments/reply", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-User-Id": user.uid,
        },
        body: JSON.stringify({
          parent_id: data.parent_id,
          target_type: targetType,
          target_id: articleId,
          body: data.body,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "답글 작성 실패");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/comments", targetType, articleId],
      });
      setReplyContent("");
      setReplyingTo(null);
      toast({
        title: "답글 작성 완료",
        description: "답글이 등록되었습니다.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "오류",
        description: error.message || "답글 작성에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !content.trim() || createMutation.isPending) return;

    // Validate user authentication
    if (!user.uid) {
      console.error("[Comments] User object missing uid:", user);
      toast({
        title: "오류",
        description:
          "사용자 정보를 불러올 수 없습니다. 로그인 상태를 확인해주세요.",
        variant: "destructive",
      });
      return;
    }

    // Debug logging
    console.log("[Comments] Submitting comment:", {
      userId: user.uid,
      userEmail: user.email || "no email",
      hasUser: !!user,
    });

    // Validate content length
    if (content.trim().length < 2) {
      toast({
        title: "댓글은 최소 2자 이상 입력해주세요.",
        variant: "destructive",
      });
      return;
    }

    // Ensure we have a valid uid
    if (!user.uid) {
      console.error("[Comments] user.uid is missing!", { user });
      toast({
        title: "오류",
        description: "사용자 ID를 찾을 수 없습니다. 다시 로그인해주세요.",
        variant: "destructive",
      });
      return;
    }

    console.log("[Comments] Creating mutation with data:", {
      userId: user.uid,
      userEmail: user.email || "no email",
      userDisplayName: user.displayName || "no display name",
      hasUid: !!user.uid,
    });

    createMutation.mutate({
      userId: user.uid,
      userEmail: user.email || "",
      userDisplayName: user.displayName || user.email || "익명",
      userPhotoUrl: user.photoURL || null,
      content: content.trim(),
      isPrivate: isPrivate && canUsePrivateComment,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with Sort Options */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-ink-2" />
          <h3 className="text-lg font-semibold text-ink-1">
            {responseCopy.header} <span className="text-ink-3">{comments.length}</span>
          </h3>
        </div>
        {comments.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setSortOrder(sortOrder === "latest" ? "oldest" : "latest")
              }
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded"
            >
              <ArrowUpDown className="h-3.5 w-3.5" />
              {sortOrder === "latest" ? "최신순" : "등록순"}
            </button>
          </div>
        )}
      </div>

      {/* Comment Form */}
      {authLoading ? (
        <div className="text-center py-4 text-ink-3 text-sm">로딩 중...</div>
      ) : !user ? (
        <div className="bg-surface-1 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-ink-2">
            <LogIn className="h-4 w-4" />
            <span className="text-sm">
              {responseCopy.login}
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAuthDialogOpen(true)}
            data-testid="button-login-response"
          >
            로그인
          </Button>
        </div>
      ) : (
        <div className="bg-surface-0 rounded-lg border border-line-subtle p-4">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex gap-3">
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage src={user.photoURL || undefined} />
                <AvatarFallback>
                  {(user.displayName || user.email || "U")[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-2 max-w-[70%]">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={responseCopy.placeholder}
                  rows={2}
                  className="resize-none text-sm rounded-lg"
                  required
                  data-testid="input-comment-content"
                />
                {canUsePrivateComment && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      id="private-comment"
                      checked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                      className="h-3 w-3"
                    />
                    <label
                      htmlFor="private-comment"
                      className="cursor-pointer flex items-center gap-1"
                    >
                      <Lock className="h-3 w-3" />
                      비밀댓글 (작성자에게만 보임)
                    </label>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={createMutation.isPending || !content.trim()}
                    className="h-8 px-4 rounded-full"
                    data-testid="button-submit-comment"
                  >
                    <Send className="h-3.5 w-3.5" />
                    <span>{responseCopy.submit}</span>
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-8 text-ink-3 text-sm">
            댓글을 불러오는 중...
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 space-y-4">
            <div className="flex flex-col items-center gap-3">
              <MessageSquare className="h-12 w-12 text-muted-foreground/50" />
              <div className="space-y-1">
                <p className="text-base font-medium text-foreground">
                  {responseCopy.emptyTitle}
                </p>
                <p className="text-sm text-muted-foreground">
                  {responseCopy.emptyDescription}
                </p>
              </div>
            </div>
          </div>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              className="flex gap-3 py-3 border-b border-line-subtle last:border-0"
              data-testid={`comment-${comment.id}`}
            >
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage
                  src={
                    normalizeImageUrl(
                      (comment as any).userAvatarUrl || comment.userPhotoUrl
                    ) || undefined
                  }
                />
                <AvatarFallback className="text-xs">
                  {((comment as any).userName ||
                    comment.userDisplayName ||
                    "U")[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p
                        className="font-medium text-sm text-foreground truncate"
                        data-testid="text-comment-author"
                      >
                        {(comment as any).userName ||
                          comment.userDisplayName ||
                          "익명"}
                      </p>
                      {/* 동네 정보 (일단 placeholder, 나중에 users 테이블에서 가져오기) */}
                      {(comment as any).userSuburb && (
                        <span className="text-xs text-muted-foreground">
                          {(comment as any).userSuburb}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(comment.createdAt), {
                        addSuffix: true,
                        locale: ko,
                      })}
                    </p>
                  </div>
                </div>
                {(comment as any).isPrivate && !isPostAuthor ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground italic py-2">
                    <Lock className="h-3 w-3" />
                    <span>비밀댓글입니다 (작성자에게만 보입니다)</span>
                  </div>
                ) : (
                  <p
                    className="text-sm text-ink-2 whitespace-pre-wrap break-words"
                    data-testid="text-comment-content"
                  >
                    {comment.content}
                    {(comment as any).isPrivate && isPostAuthor && (
                      <span className="ml-2 text-xs text-muted-foreground italic">
                        (비밀댓글)
                      </span>
                    )}
                  </p>
                )}
                {/* Action Buttons - 당근 스타일 */}
                <div className="flex items-center gap-3 mt-2">
                  <button
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={async () => {
                      if (!user) {
                        toast({
                          title: "로그인 필요",
                          description: "좋아요를 하려면 로그인이 필요합니다.",
                        });
                        return;
                      }

                      try {
                        const token = await user.getIdToken();
                        const response = await fetch("/api/comments/like", {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                            "X-User-Id": user.uid,
                          },
                          body: JSON.stringify({
                            comment_id: comment.id,
                            target_type: targetType,
                            target_id: articleId,
                          }),
                        });

                        if (!response.ok) {
                          throw new Error("좋아요 실패");
                        }

                        const data = await response.json();
                        // Refetch comments to update like count
                        queryClient.invalidateQueries({
                          queryKey: [
                            "/api/comments",
                            targetType,
                            articleId,
                            user?.uid,
                            sortOrder,
                          ],
                        });
                      } catch (error) {
                        toast({
                          title: "오류",
                          description: "좋아요 처리 중 오류가 발생했습니다.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                    <span>{(comment as any).likeCount || 0}</span>
                  </button>
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => {
                      if (!user) {
                        toast({
                          title: "로그인 필요",
                          description: "답글을 작성하려면 로그인이 필요합니다.",
                        });
                        return;
                      }
                      setReplyingTo(
                        replyingTo === comment.id ? null : comment.id
                      );
                      setReplyContent("");
                    }}
                  >
                    답글
                  </button>
                  <ReportButton
                    reportedType="comment"
                    reportedId={comment.id}
                    reportedUserId={String((comment as any).userId ?? "")}
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                  />
                </div>
              </div>

              {/* Reply Input */}
              {replyingTo === comment.id && (
                <div className="mt-3 ml-12 space-y-2">
                  <Textarea
                    placeholder="답글을 입력하세요..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    className="min-h-[80px] text-sm"
                    rows={3}
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!replyContent.trim()) {
                          toast({
                            title: "내용을 입력하세요",
                            variant: "destructive",
                          });
                          return;
                        }
                        replyMutation.mutate({
                          parent_id:
                            typeof comment.id === "string"
                              ? parseInt(comment.id, 10) || comment.id
                              : comment.id,
                          body: replyContent.trim(),
                        });
                      }}
                      disabled={replyMutation.isPending}
                    >
                      {replyMutation.isPending ? "등록 중..." : "답글 등록"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyContent("");
                      }}
                    >
                      취소
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
    </div>
  );
}
